import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SKIP_TENANT_AUTH_REFRESH } from '../interceptors/auth-refresh-context';
import { TenantSessionRefreshService } from '../services/session/tenant-session-refresh.service';
import { TenantAuthService } from '../services/tenant-auth.service';

type TenantAuthSession = {
  authenticated?: boolean | null;
  accountType?: string | null;
  roles?: string[] | null;
};

const VERIFY_TIMEOUT_MS = 8000;
const VERIFY_SETTLE_DELAYS_MS = [0, 250, 750, 1250] as const;
const VERIFY_AFTER_REFRESH_DELAYS_MS = [0, 750] as const;

export const brandedTenantPortalGuard: CanActivateFn = async (_route, state) => {
  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  const http = inject(HttpClient);
  const auth = inject(TenantAuthService);
  const router = inject(Router);
  const sessionRefresh = inject(TenantSessionRefreshService);
  const host = window.location.hostname.toLowerCase();
  const path = window.location.pathname;
  if (path === '/login' || path.startsWith('/login/')) {
    return true;
  }

  const tenantHost = baseHostname(environment.tenantAppUrl);
  if (!tenantHost) {
    return requireTenantSession(http, auth, sessionRefresh, state.url, router);
  }

  if (isLocalHost(host)) {
    return requireTenantSession(http, auth, sessionRefresh, state.url, router);
  }

  if (host.endsWith(`.${tenantHost}`)) {
    return requireTenantSession(http, auth, sessionRefresh, state.url, router);
  }

  if (host === tenantHost) {
    const url = new URL(window.location.href);
    const workspace = url.searchParams.get('workspace') || url.searchParams.get('org');
    const orgId = url.searchParams.get('orgId');
    if (workspace?.trim()) {
      const brandedUrl = new URL(environment.tenantAppUrl);
      brandedUrl.hostname = `${workspace.trim().toLowerCase()}.${brandedUrl.hostname}`;
      brandedUrl.pathname = url.pathname;
      brandedUrl.search = url.search;
      brandedUrl.hash = url.hash;
      window.location.href = brandedUrl.toString();
      return false;
    }
    if (orgId?.trim()) {
      return requireTenantSession(http, auth, sessionRefresh, state.url, router);
    }

    return requireTenantSession(http, auth, sessionRefresh, state.url, router);
  }

  return requireTenantSession(http, auth, sessionRefresh, state.url, router);
};

async function requireTenantSession(
  http: HttpClient,
  auth: TenantAuthService,
  sessionRefresh: TenantSessionRefreshService,
  returnUrl: string,
  router: Router,
): Promise<boolean | UrlTree> {
  const session = await resolveTenantSession(http, sessionRefresh);

  if (!session?.authenticated) {
    auth.redirectToLogin(returnUrl || '/dashboard');
    return false;
  }

  // Membership is scoped to the selected workspace. A global staff role does not invalidate a tenancy.
  const context = await firstValueFrom(http.get<{ organizations?: Array<{ orgId: string; orgRole?: string; portalMode?: string; portalSlug?: string }> }>(
    `${environment.apiGatewayUrl}/property-service/organization/context`, { withCredentials: true },
  ).pipe(timeout(VERIFY_TIMEOUT_MS))).catch(() => null);
  const url = new URL(returnUrl, window.location.origin);
  const orgId = url.searchParams.get('orgId') || new URLSearchParams(window.location.search).get('orgId');
  const tenantHost = baseHostname(environment.tenantAppUrl);
  const brandedSlug = tenantHost && window.location.hostname.endsWith(`.${tenantHost}`)
    ? window.location.hostname.slice(0, -(tenantHost.length + 1)) : null;
  const memberships = context?.organizations ?? [];
  const tenantMemberships = memberships.filter(org => org.orgRole === 'TENANT_PORTAL' || org.portalMode === 'TENANT');
  const selected = tenantMemberships.find(org => (!orgId || org.orgId === orgId) && (!brandedSlug || org.portalSlug === brandedSlug));
  if (selected) return true;
  const destination = orgId ? tenantMemberships.find(org => org.orgId === orgId) : undefined;
  if (destination && brandedSlug) {
    const next = new URL(environment.tenantAppUrl);
    if (destination.portalSlug && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(destination.portalSlug)) {
      next.hostname = `${destination.portalSlug}.${next.hostname}`;
    }
    next.pathname = url.pathname;
    next.search = url.search;
    next.searchParams.delete('workspace');
    next.searchParams.delete('org');
    window.location.href = next.toString();
    return false;
  }

  // The dashboard can display pending invitations before a workspace is accepted.
  if (url.pathname === '/dashboard') return true;
  return router.createUrlTree(['/dashboard']);
}

async function resolveTenantSession(
  http: HttpClient,
  sessionRefresh: TenantSessionRefreshService,
): Promise<TenantAuthSession | null> {
  for (const delay of VERIFY_SETTLE_DELAYS_MS) {
    if (delay > 0) {
      await wait(delay);
    }

    const session = await verifyTenantSession(http);
    if (session?.authenticated) {
      return session;
    }
  }

  await firstValueFrom(sessionRefresh.refresh()).catch(() => null);
  await wait(300);

  for (const delay of VERIFY_AFTER_REFRESH_DELAYS_MS) {
    if (delay > 0) {
      await wait(delay);
    }

    const session = await verifyTenantSession(http);
    if (session?.authenticated) {
      return session;
    }
  }

  return null;
}

function verifyTenantSession(http: HttpClient): Promise<TenantAuthSession | null> {
  return firstValueFrom(
    http.get<TenantAuthSession>(`${environment.apiGatewayUrl}/api/v1/user/auth-verify`, {
      context: new HttpContext().set(SKIP_TENANT_AUTH_REFRESH, true),
      withCredentials: true,
    }).pipe(timeout(VERIFY_TIMEOUT_MS)),
  ).catch(() => null);
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function redirectToTenantLogin(returnUrl: string, portal: string): void {
  const url = new URL('/login', environment.tenantAppUrl);
  url.searchParams.set('returnUrl', returnUrl || '/dashboard');
  url.searchParams.set('portalMismatch', portal);
  window.location.href = url.toString();
}

function baseHostname(value: string): string | null {
  try {
    return new URL(value).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function isLocalHost(host: string): boolean {
  return host === 'localhost' || host.includes('localhost') || /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
}
